// =============================================================
// Shared constants
// =============================================================
import type { Priority, Status, Category, Role } from "./api";

export const PRIORITIES: Priority[] = ["CRITICAL", "HIGH", "MEDIUM", "LOW"];
export const STATUSES: Status[] = [
  "OPEN",
  "IN_PROGRESS",
  "ON_HOLD",
  "RESOLVED",
  "CLOSED",
];
export const CATEGORIES: Category[] = [
  "SYSTEM",
  "HARDWARE",
  "SOFTWARE",
  "NETWORK",
  "ACCESS",
  "OTHER",
];
export const ROLES: Role[] = ["AGENT", "IT", "ADMIN"];

interface PriorityMeta {
  label: string;
  color: string;
  dot: string;
  ring: string;
  text: string;
  bg: string;
  border: string;
  description: string;
}

export const PRIORITY_META: Record<Priority, PriorityMeta> = {
  CRITICAL: { label: "Critical", color: "#fb0b14", dot: "bg-critical-400", ring: "ring-critical-400/50", text: "text-critical-200", bg: "bg-critical-500/15", border: "border-critical-400/40", description: "Operations are completely stopped. Large-scale impact with no workaround. Immediate action required." },
  HIGH: { label: "High", color: "#f97316", dot: "bg-orange-400", ring: "ring-orange-400/50", text: "text-orange-200", bg: "bg-orange-500/15", border: "border-orange-400/40", description: "Major impact on a team, campaign, or multiple agents. Operations continue but are severely affected." },
  MEDIUM: { label: "Medium", color: "#eab308", dot: "bg-yellow-400", ring: "ring-yellow-400/50", text: "text-yellow-200", bg: "bg-yellow-500/15", border: "border-yellow-400/40", description: "Moderate impact affecting individual users or a small group. A workaround may exist." },
  LOW: { label: "Low", color: "#22c55e", dot: "bg-emerald-400", ring: "ring-emerald-400/50", text: "text-emerald-200", bg: "bg-emerald-500/15", border: "border-emerald-400/40", description: "Minor impact with little effect on productivity." },
};

export const PRIORITY_TEMPLATES: Record<Priority, string[]> = {
  CRITICAL: [
    "Entire internet outage",
    "All agents are unable to log into some of the existing systems",
    "Line 2 down",
    "All existing systems are down or in maintenance",
    "Power outage affecting operations",
    "Computer shutdown can't be used",
    "Server Shutdown",
    "Need to add information on operational systems"
  ],
  HIGH: [
    "Cannot receive calls, just missed a call",
    "Multiple agents experiencing call drops",
    "CRM inaccessible for a department",
    "Black & White Screen of a computer",
    "Synology files can't sync",
    "All wiring related to the station",
    "The system unit is producing a beeping or unusual sound."
  ],
  MEDIUM: [
    "Single agent unable to log in",
    "Headset issue (client can't hear)",
    "Mouse issue",
    "Slow CRM performance",
    "Email problems",
    "Microsoft Problems",
    "Intermittent call quality issues",
    "Monitor glitching"
  ],
  LOW: [
    "Printer issues",
    "Non-critical software error",
    "Workstation customization request",
    "Call forwarding request",
    "Software installation request",
    "Access permission request",
    "Permission to restart the PC",
    "The user thinks someone is using her/his computer, unusual activities happen on the PC"
  ],
};

interface StatusMeta {
  label: string;
  color: string;
  text: string;
  bg: string;
  border: string;
}

export const STATUS_META: Record<Status, StatusMeta> = {
  OPEN: { label: "Open", color: "#0ea5e9", text: "text-sky-200", bg: "bg-sky-500/15", border: "border-sky-400/40" },
  IN_PROGRESS: { label: "In Progress", color: "#6366f1", text: "text-indigo-200", bg: "bg-indigo-500/15", border: "border-indigo-400/40" },
  ON_HOLD: { label: "On Hold", color: "#64748b", text: "text-slate-200", bg: "bg-slate-500/15", border: "border-slate-400/40" },
  RESOLVED: { label: "Resolved", color: "#10b981", text: "text-emerald-200", bg: "bg-emerald-500/15", border: "border-emerald-400/40" },
  CLOSED: { label: "Closed", color: "#ef4444", text: "text-red-200", bg: "bg-red-500/15", border: "border-red-400/40" },
};

export const CATEGORY_META: Record<Category, { label: string; description?: string }> = {
  SYSTEM: { label: "System", description: "Internal applications created by our web developers, custom tools, and operational processes." },
  HARDWARE: { label: "Hardware", description: "Physical equipment, computer parts, and electronic devices." },
  SOFTWARE: { label: "Software", description: "Programs, desktop applications, and digital instructions." },
  NETWORK: { label: "Network", description: "Connectivity, internet access, VPN, and network infrastructure." },
  ACCESS: { label: "Access", description: "Permissions, accounts, passwords, and access rights." },
  OTHER: { label: "Other", description: "General inquiries and uncategorized issues." },
};

// SLA target hours (kept in sync with backend services/sla.php)
export const SLA_HOURS: Record<Priority, number> = { CRITICAL: 4, HIGH: 8, MEDIUM: 24, LOW: 48 };

export const ISSUE_CATEGORIES: Record<string, Category> = {
  // SOFTWARE
  "All existing systems are down or in maintenance": "SOFTWARE",
  "Need to add information on operational systems": "SOFTWARE",
  "Slow CRM performance": "SOFTWARE",
  "Email problems": "SOFTWARE",
  "Microsoft Problems": "SOFTWARE",
  "Non-critical software error": "SOFTWARE",
  "Workstation customization request": "SOFTWARE",
  "Software installation request": "SOFTWARE",

  // HARDWARE
  "Power outage affecting operations": "HARDWARE",
  "Computer shutdown can't be used": "HARDWARE",
  "Black & White Screen of a computer": "HARDWARE",
  "All wiring related to the station": "HARDWARE",
  "The system unit is producing a beeping or unusual sound.": "HARDWARE",
  "Headset issue (client can't hear)": "HARDWARE",
  "Mouse issue": "HARDWARE",
  "Monitor glitching": "HARDWARE",
  "Printer issues": "HARDWARE",

  // SYSTEM
  "CRM inaccessible for a department": "SYSTEM",
  "Server Shutdown": "SYSTEM",
  "Synology files can't sync": "SYSTEM",

  // NETWORK
  "Entire internet outage": "NETWORK",
  "Line 2 down": "NETWORK",
  "Cannot receive calls, just missed a call": "NETWORK",
  "Multiple agents experiencing call drops": "NETWORK",
  "Intermittent call quality issues": "NETWORK",
  "Call forwarding request": "NETWORK",

  // ACCESS
  "All agents are unable to log into some of the existing systems": "ACCESS",
  "Single agent unable to log in": "ACCESS",
  "Access permission request": "ACCESS",
  "Permission to restart the PC": "ACCESS",
  "The user thinks someone is using her/his computer, unusual activities happen on the PC": "ACCESS",
};
